import { AppContent, AppShell as AppShellRoot, useAppShell } from "@sylviq/ui-react-web/components/shell/AppShell";
import { ProductLockup } from "@sylviq/ui-react-web/components/shell/ProductLockup";
import { NavItem, NavSection } from "@sylviq/ui-react-web/components/shell/SidebarNav";
import {
  AppNavigation,
  type AppNavigationDestination,
  type AppNavigationSection,
} from "@sylviq/ui-react-web/blocks/navigation";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Header } from "@sylviq/ui-react-web/components/ui/header";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Leaf, LogOut, Menu, UserRound } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { platformAccountUrl, redirectToPlatformSignIn } from "@/lib/platform-signin";
import { listExtensionInstallations, type ExtensionInstallation } from "@/lib/api";
import { useSession } from "@/lib/session";
import { useOrganization } from "@/lib/organization";
import { ACCOUNTING_APP_SETTINGS, ACCOUNTING_DOMAINS, domainForPath, type DomainItem, type AccountingDomain } from "./navigation";
import { destinationSearch } from "./destination-search";

const NAVIGATION_ORIGIN = "https://accounting.invalid";

interface NavigationDestination {
  to: string;
}

function closeNavigationOnNarrow(close:()=>void) {
  if(window.matchMedia("(max-width: 42rem)").matches)window.setTimeout(close,0);
}

function itemCurrent(item: DomainItem, pathname: string, search: Record<string, unknown>) {
  const url = new URL(item.to, NAVIGATION_ORIGIN);
  const params = Object.fromEntries(url.searchParams);
  const pathMatches = pathname === url.pathname || (url.pathname !== "/" && pathname.startsWith(`${url.pathname}/`));
  return pathMatches && Object.entries(params).every(([key, value]) => search[key] === value);
}

function domainSection(
  domain: AccountingDomain,
  active: boolean,
  pathname: string,
  search: Record<string, unknown>,
): AppNavigationSection<NavigationDestination> {
  return {
    id: domain.id,
    label: domain.label,
    icon: domain.icon,
    active,
    destination: { to: domain.to },
    children: domain.items
      .filter((item) => !item.hidden)
      .map((item) => ({
        id: item.to,
        label: item.label,
        active: itemCurrent(item, pathname, search),
        secondary: item.secondary,
        destination: { to: item.to },
      })),
  };
}

function FooterDestination({ domain, active }: { domain: AccountingDomain; active: boolean }) {
  const { close } = useAppShell();
  const url = new URL(domain.to, NAVIGATION_ORIGIN);
  const params = Object.fromEntries(url.searchParams);
  return (
    <NavItem asChild icon={domain.icon} label={domain.label} active={active} onClick={() => window.setTimeout(close, 0)}>
      <Link to={url.pathname} search={(previous) => destinationSearch(previous, params)} />
    </NavItem>
  );
}

interface ExtensionNavItem {
  id: string;
  label: string;
  href: string;
  secondary: boolean;
  parentSectionId?: string;
}

const STATIC_PRIMARY_SECTIONS = new Set([
  ...ACCOUNTING_DOMAINS.map((domain) => domain.id),
  ACCOUNTING_APP_SETTINGS.id,
]);

function extensionSectionId(installation: ExtensionInstallation, navigationId: string): string {
  return `extension:${installation.publisher}/${installation.key}:${navigationId}`;
}

function extensionHref(
  installation: ExtensionInstallation,
  views: ReadonlySet<string>,
  destination: Record<string, unknown>,
): string | null {
  if (
    destination.type === "view" &&
    typeof destination.viewId === "string" &&
    views.has(destination.viewId)
  ) {
    return `/extensions/${encodeURIComponent(installation.publisher)}/${encodeURIComponent(installation.key)}/${encodeURIComponent(destination.viewId)}`;
  }
  if (
    destination.type === "path" &&
    typeof destination.path === "string" &&
    destination.path.startsWith("/") &&
    !destination.path.startsWith("//")
  ) {
    return destination.path;
  }
  return null;
}

function v2ExtensionNavigation(installation: ExtensionInstallation): ExtensionNavItem[] {
  const metadata = installation.v2;
  if (!metadata) return [];
  const views = new Set<string>();
  for (const value of metadata.views ?? []) {
    if (!value || typeof value !== "object") continue;
    const view = value as { id?: unknown };
    if (typeof view.id === "string") views.add(view.id);
  }
  const navigation = new Map<string, {
    label: string;
    parent?: string;
    destination: Record<string, unknown>;
  }>();
  for (const value of metadata.navigation ?? []) {
    if (!value || typeof value !== "object") continue;
    const item = value as {
      id?: unknown;
      app?: unknown;
      label?: unknown;
      parent?: unknown;
      destination?: unknown;
    };
    if (
      typeof item.id !== "string" ||
      item.app !== "niche-accounting" ||
      typeof item.label !== "string" ||
      !item.label.trim() ||
      !item.destination ||
      typeof item.destination !== "object"
    ) {
      continue;
    }
    navigation.set(item.id, {
      label: item.label,
      ...(typeof item.parent === "string" ? { parent: item.parent } : {}),
      destination: item.destination as Record<string, unknown>,
    });
  }
  const items: ExtensionNavItem[] = [];
  const seen = new Set<string>();
  for (const value of metadata.ui ?? []) {
    if (!value || typeof value !== "object") continue;
    const contribution = value as {
      app?: unknown;
      slot?: unknown;
      navigationId?: unknown;
    };
    if (
      contribution.app !== "niche-accounting" ||
      (contribution.slot !== "navigation.primary" && contribution.slot !== "navigation.secondary") ||
      typeof contribution.navigationId !== "string"
    ) {
      continue;
    }
    const item = navigation.get(contribution.navigationId);
    if (!item) continue;
    const secondary = contribution.slot === "navigation.secondary";
    if (!secondary && item.parent !== undefined) continue;
    if (secondary && item.parent === undefined) continue;
    const parentSectionId = item.parent === undefined
      ? undefined
      : STATIC_PRIMARY_SECTIONS.has(item.parent)
        ? item.parent
        : extensionSectionId(installation, item.parent);
    const href = extensionHref(installation, views, item.destination);
    const id = extensionSectionId(installation, contribution.navigationId);
    if (!href || seen.has(id)) continue;
    seen.add(id);
    items.push({
      id,
      label: item.label,
      href,
      secondary,
      ...(parentSectionId === undefined ? {} : { parentSectionId }),
    });
  }
  return items;
}

function extensionNavigation(installations: ExtensionInstallation[]): ExtensionNavItem[] {
  const items = new Map<string, ExtensionNavItem>();
  for (const installation of installations) {
    if (
      installation.state !== "enabled" ||
      !installation.apps?.includes("niche-accounting")
    ) {
      continue;
    }
    if (installation.api_version === "2.0") {
      for (const item of v2ExtensionNavigation(installation)) {
        items.set(item.id, item);
      }
      continue;
    }
    for (const contribution of installation.ui ?? []) {
      if (contribution.slot !== "accounting.navigation.v1") continue;
      const view = contribution.view;
      if (!view || typeof view !== "object") continue;
      const candidate = view as { type?: unknown; label?: unknown; href?: unknown };
      if (candidate.type !== "link" || typeof candidate.label !== "string" || typeof candidate.href !== "string") continue;
      if (!candidate.href.startsWith("/")) continue;
      const id = `extension:${candidate.label}:${candidate.href}`;
      items.set(id, {
        id,
        label: candidate.label,
        href: candidate.href,
        secondary: false,
      });
    }
  }
  return [...items.values()];
}

function useExtensionNavigation(): ExtensionNavItem[] {
  const { scope } = useOrganization();
  const [items, setItems] = useState<ExtensionNavItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!scope?.organization_id) {
      setItems([]);
      return;
    }
    listExtensionInstallations(scope.organization_id)
      .then((installations) => {
        if (!cancelled) setItems(extensionNavigation(installations));
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [scope?.organization_id]);
  return items;
}

function extensionSections(
  items: ExtensionNavItem[],
  pathname: string,
): AppNavigationSection<NavigationDestination>[] {
  return items
    .filter((item) => !item.secondary)
    .map((item) => ({
      id: item.id,
      label: item.label,
      icon: Leaf,
      active: pathname === item.href,
      destination: { to: item.href },
    }));
}

/**
 * Add v2 primary sections and attach secondary destinations to their declared
 * parent. Static sections already own the same child-navigation behavior in
 * the shared `AppNavigation` block; this only feeds extension children into it.
 */
function mergeExtensionNavigation(
  base: AppNavigationSection<NavigationDestination>[],
  items: ExtensionNavItem[],
  pathname: string,
): AppNavigationSection<NavigationDestination>[] {
  const sections = base.map((section) => ({
    ...section,
    children: section.children ? [...section.children] : undefined,
  }));
  const merged = [...sections, ...extensionSections(items, pathname)];
  const byId = new Map(merged.map((section) => [section.id, section]));
  for (const item of items.filter((candidate) => candidate.secondary)) {
    if (!item.parentSectionId) continue;
    const parent = byId.get(item.parentSectionId);
    if (!parent) continue;
    if (!parent.children) {
      if (!parent.destination) continue;
      parent.children = [{
        id: `${parent.id}:self`,
        label: parent.label,
        active: pathname === parent.destination.to,
        destination: parent.destination,
      }];
    }
    const active = pathname === item.href;
    parent.children = [...parent.children, {
      id: item.id,
      label: item.label,
      active,
      secondary: true,
      destination: { to: item.href },
    }];
    if (active) parent.active = true;
  }
  return merged;
}

function FooterSettings({
  active,
  pathname,
  items,
}: {
  active: boolean;
  pathname: string;
  items: ExtensionNavItem[];
}) {
  if (items.length === 0) {
    return <FooterDestination domain={ACCOUNTING_APP_SETTINGS} active={active} />;
  }
  const { close } = useAppShell();
  const current = active || items.some((item) => pathname === item.href);
  return (
    <NavSection id="settings" icon={ACCOUNTING_APP_SETTINGS.icon} label={ACCOUNTING_APP_SETTINGS.label} current={current}>
      <NavItem asChild icon={ACCOUNTING_APP_SETTINGS.icon} label={ACCOUNTING_APP_SETTINGS.label} active={active} onClick={() => window.setTimeout(close, 0)}>
        <Link to={ACCOUNTING_APP_SETTINGS.to} />
      </NavItem>
      {items.map((item) => (
        <NavItem key={item.id} asChild icon={Leaf} label={item.label} active={pathname === item.href} onClick={() => window.setTimeout(close, 0)}>
          <Link to={item.href} />
        </NavItem>
      ))}
    </NavSection>
  );
}

function entityChoices(entities: { id: string; name: string; is_primary?: boolean }[]) {
  return [...entities].sort((left, right) => Number(Boolean(right.is_primary)) - Number(Boolean(left.is_primary)) || left.name.localeCompare(right.name));
}

function accountInitials(name: string): string {
  const parts = name.trim().split(/[\s._-]+/).filter(Boolean);
  return parts.length > 1
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : (name.trim() || "?").slice(0, 2).toUpperCase();
}

function Shell({children}:{children?:ReactNode}) {
  const {state,signOut}=useSession();
  const {scope,legalEntities,selectLegalEntity}=useOrganization();
  const navigate=useNavigate();
  const {pathname,search}=useRouterState({select:s=>s.location});
  const {open,close}=useAppShell();
  const current=domainForPath(pathname,search);
  const entities=entityChoices(legalEntities);
  const user=state.status==="authenticated"?state.user:null;
  const extensionItems=useExtensionNavigation();
  const settingsActive=pathname===ACCOUNTING_APP_SETTINGS.to||pathname.startsWith(`${ACCOUNTING_APP_SETTINGS.to}/`);
  const settingsChildren=extensionItems.filter((item)=>item.secondary&&item.parentSectionId===ACCOUNTING_APP_SETTINGS.id);
  const settingsSectionActive=settingsActive||settingsChildren.some((item)=>pathname===item.href);
  const sections=mergeExtensionNavigation(
    ACCOUNTING_DOMAINS.map((domain)=>domainSection(domain,current?.id===domain.id,pathname,search as Record<string,unknown>)),
    extensionItems,
    pathname,
  );
  const renderDestination=(destination: AppNavigationDestination<NavigationDestination>) => {
    const url = new URL(destination.meta?.to ?? "/", NAVIGATION_ORIGIN);
    const params = Object.fromEntries(url.searchParams);
    return (
      <Link
        to={url.pathname}
        search={(previous) => destinationSearch(previous, params)}
        onClick={() => closeNavigationOnNarrow(close)}
      />
    );
  };
  const AccountAvatar=({className}:{className?:string})=>(
    <span className={`${className??""} account-avatar`} aria-hidden="true">{accountInitials(user?.username??"")}</span>
  );
  const chooseEntity=(legalEntityId:string)=>{
    if(!selectLegalEntity(legalEntityId))return;
    void navigate({
      to:".",
      search:(previous:Record<string,unknown>)=>({...previous,legal_entity_id:legalEntityId}),
      replace:true,
    });
  };
  return <>
    <Header left={<><Button type="button" size="icon" variant="ghost" className="app-menu-button" aria-label="Open navigation" onClick={open}><Menu className="size-5"/></Button>{entities.length>0?<select aria-label="Legal entity" className="select entity-switcher" value={scope?.legal_entity_id??entities[0].id} onChange={(event)=>chooseEntity(event.target.value)}>{entities.map((entity)=><option key={entity.id} value={entity.id}>{entity.name}</option>)}</select>:<p className="surface max-w-[9rem] truncate rounded-xl px-2.5 py-1.5 text-xs sm:max-w-xs sm:px-3">Legal entity</p>}</>}
      />
    <AppNavigation
      label="Accounting sections"
      sidebarLabel="Application navigation"
      className="app-sidebar--compact accounting-sidebar"
      sidebarProps={{style:{maxHeight:"none"}}}
      sections={sections}
      renderDestination={renderDestination}
      lockup={<div className="flex min-w-0 flex-col"><ProductLockup asChild mark={<><img src="/niche-logo.svg" alt="" className="size-8 shrink-0 dark:hidden"/><img src="/niche-logo-dark.svg" alt="" className="hidden size-8 shrink-0 dark:block"/></>} wordmark={<span className="font-serif text-lg tracking-tight">Niche Accounting</span>}><Link to="/" aria-label="Niche Accounting home" onClick={() => window.setTimeout(close, 0)}/></ProductLockup></div>}
      footer={<><FooterSettings active={settingsSectionActive} pathname={pathname} items={settingsChildren}/>{user?<NavSection id="account" icon={AccountAvatar} label={user.username}><NavItem asChild icon={UserRound} label="Account"><a href={platformAccountUrl()}/></NavItem><NavItem icon={LogOut} label="Sign out" onClick={()=>void signOut().then(redirectToPlatformSignIn)}/></NavSection>:null}</>}
    />
    <AppContent id="accounting-main" className="min-[42.0625rem]:ms-[7.25rem] min-[42.0625rem]:pe-10 min-[42.0625rem]:[&_.app-content-inner]:mx-0 min-[42.0625rem]:[&_.app-content-inner]:w-full min-[42.0625rem]:[&_.app-content-inner]:max-w-none"><div key={scope?.legal_entity_id??"none"} className="grid min-w-0 grid-cols-1 gap-6">{children??<Outlet/>}</div></AppContent>
  </>;
}
export function AppShell({children}:{children?:ReactNode}) {
  return <AppShellRoot skipToId="accounting-main"><Shell>{children}</Shell></AppShellRoot>;
}
